import { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Row, Spinner } from "react-bootstrap";
import { ExternalLink, FolderHeart, Home, UserRound } from "lucide-react";
import api from "Common/api";
import "../../Homeowner/RecommendedProperties/recommended-properties.css";

type SharedProperty = {
  listingKey?: string;
  listingId?: string;
  thumbnailUrl?: string;
  propertyType?: string;
  propertySubType?: string;
  listPrice?: number;
  bedsTotal?: number;
  bathsTotal?: number;
  sqftTotal?: number;
  UnparsedAddress?: string;
  StreetAddress?: string;
  City?: string;
  State?: string;
  PostalCode?: string;
  city?: string;
  state?: string;
  postalCode?: string;
};

type SharedCollection = {
  id: number;
  name: string;
  agentName?: string;
  properties: SharedProperty[];
};

const formatPrice = (value?: number) => {
  if (!Number(value)) return "Price on request";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(value));
};

const propertyAddress = (property: SharedProperty) =>
  [
    property.UnparsedAddress || property.StreetAddress,
    property.City || property.city,
    property.State || property.state,
    property.PostalCode || property.postalCode,
  ].filter(Boolean).join(", ") || "Address available in property details";

const propertyUrl = (property: SharedProperty) => {
  const id = property.listingId || property.listingKey;
  return `https://www.relibuild.com/property-detail/property-${encodeURIComponent(String(id || ""))}`;
};

/** Shows a compact recommendation preview and a useful empty state on the homeowner dashboard. */
export default function RecommendedPropertiesPreview() {
  const [collections, setCollections] = useState<SharedCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.get("/agent/property-collections/homeowner")
      .then((response: any) => {
        const data = response?.data?.data ?? response?.data;
        setCollections(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setCollections([]);
        setFailed(true);
      })
      .finally(() => setLoading(false));
  }, []);

  const recommendations = collections.flatMap((collection) =>
    (collection.properties || []).map((property) => ({ collection, property }))
  ).slice(0, 4);

  return (
    <section className="mt-4 homeowner-dashboard-recommendations">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <div>
          <div className="recommendations-kicker"><FolderHeart size={17} /> CURATED FOR YOU</div>
          <h4 className="mb-0 mt-1">Recommended Properties</h4>
        </div>
        <Button href="/recommended-properties" variant="outline-primary" size="sm">
          View all recommendations
        </Button>
      </div>
      {loading ? (
        <div className="homeowner-discovery-empty"><Spinner animation="border" size="sm" /> Loading recommended properties…</div>
      ) : recommendations.length ? (
        <Row className="g-4">
          {recommendations.map(({ collection, property }) => (
            <Col xxl={3} xl={4} md={6} key={`${collection.id}-${property.listingKey || property.listingId}`}>
              <Card className="recommended-property-card h-100">
                <div className="recommended-property-photo">
                  {property.thumbnailUrl ? <img src={property.thumbnailUrl} alt={propertyAddress(property)} /> : <div><Home size={40} /></div>}
                  <Badge bg="light" text="dark">{property.propertySubType || property.propertyType || "Property"}</Badge>
                </div>
                <Card.Body>
                  <div className="shared-by-agent mb-2"><UserRound size={15} /> {collection.agentName || "Your agent"}</div>
                  <h4>{formatPrice(property.listPrice)}</h4>
                  <p>{propertyAddress(property)}</p>
                  <div className="recommended-property-facts">
                    <span><b>{property.bedsTotal ?? "—"}</b> beds</span>
                    <span><b>{property.bathsTotal ?? "—"}</b> baths</span>
                    <span><b>{property.sqftTotal ? Number(property.sqftTotal).toLocaleString() : "—"}</b> sqft</span>
                  </div>
                  <Button as="a" href={propertyUrl(property)} target="_blank" rel="noreferrer" className="w-100 mt-3">
                    View property <ExternalLink size={15} className="ms-1" />
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <div className="homeowner-discovery-empty"><FolderHeart size={22} /><span>{failed ? "Recommended properties could not be loaded." : "Properties shared by your agent will appear here."}</span></div>
      )}
    </section>
  );
}
